import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import type { VehicleFirmwareEntry } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Firmware Entry List 情報取得API
 */
export const useFirmwareEntryListAPI = (): {
  state: APIState;
  getFirmwareEntryList: (catalogId: string) => Promise<VehicleFirmwareEntry[]>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      catalogId: string,
    ): Promise<Result<VehicleFirmwareEntry[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/vehicle_catalogs/${catalogId}/firmware_entries`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getFirmwareEntryList = useAtomCallback(
    useCallback(
      async (get, _, catalogId: string) => {
        setState('loading');
        const project = get(projectAtom);
        if (!project) {
          notifyError({
            message: t('api.fms.get_firmware_entry_list', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        const res = await request(project.id, catalogId);
        if (res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_firmware_entry_list', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return [];
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    getFirmwareEntryList,
  };
};

/**
 * Firmware Entry 個別情報取得API
 */
export const useFirmwareEntryAPI = (): {
  isLoading: boolean;
  getFirmwareEntry: (
    catalogId: string,
    firmwareReleaseId: string,
  ) => Promise<VehicleFirmwareEntry | undefined>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      catalogId: string,
      firmwareReleaseId: string,
    ): Promise<Result<VehicleFirmwareEntry, AxiosResponse>> => {
      setIsLoading(true);
      try {
        const res = await fmsAPI.get(
          `/${projectId}/vehicle_catalogs/${catalogId}/firmware_entries/${firmwareReleaseId}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      } finally {
        setIsLoading(false);
      }
    },
    [fmsAPI],
  );

  const getFirmwareEntry = useAtomCallback(
    useCallback(
      async (get, _, catalogId: string, firmwareReleaseId: string) => {
        const project = get(projectAtom);
        if (!project) {
          notifyError({
            message: t('api.fms.get_firmware_entry_list', { status: 'failed' }),
          });
          return;
        }
        const res = await request(project.id, catalogId, firmwareReleaseId);
        if (res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_firmware_entry_list', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return;
        }
        return res.value;
      },
      [notifyError, request, t, getErrorMessage],
    ),
  );

  return {
    isLoading,
    getFirmwareEntry,
  };
};
